import { FormEvent, useState } from 'react';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../buttons';
import { PasswordInput } from './PasswordInput';
import * as Sentry from '@sentry/browser';

export const PasswordUpdateForm = () => {
  const [errors, setErrors] = useState<string[]>([]);
  const [requestingUpdate, setRequestingUpdate] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const { currentUser } = useAuth();

  const meetConstraints = (password: string): boolean => {
    let alertString = 'Invalid password:\n';
    if (password.length < 8) {
      alertString = alertString.concat('- Password must be at least 8 characters\n');
    }
    if (password.length > 25) {
      alertString = alertString.concat('- Password must be at most 25 characters\n');
    }
    if (!/[A-Z]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one uppercase letter\n');
    }
    if (!/[a-z]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one lowercase letter\n');
    }
    if (!/[0-9]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one number\n');
    }
    if (!/[!@#$%^&*]/.test(password)) {
      alertString = alertString.concat('- Password must contain at least one special character (! @ # $ % ^ & *)\n');
    }

    if (alertString !== 'Invalid password:\n') {
      alert(alertString);
      return false;
    } else {
      return true;
    }
  };

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    // CHECK PASSWORDS MATCH AND ARE VALID
    if (meetConstraints(newPassword1) === false) {
      return;
    }

    if (newPassword1 !== newPassword2) {
      alert('New passwords do not match');
      return;
    }

    // CHECK CURRENT PASSWORD IS VALID
    if (currentUser !== undefined && currentUser !== null && currentUser?.email !== null) {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      try {
        reauthenticateWithCredential(currentUser, credential)
          .then(() => {
            updatePassword(currentUser, newPassword1)
              .then(() => {
                setCurrentPassword('');
                setNewPassword1('');
                setNewPassword2('');
                setRequestingUpdate(false);
                alert('Password updated successfully');
              })
              .catch((error) => {
                // auth/wrong-password: The password provided is incorrect.
                // auth/user-not-found: No user exists with the given email.
                // auth/invalid-credential: The credential is malformed or has expired.
                // auth/user-disabled: The user account has been disabled.
                // auth/operation-not-allowed: The email/password provider is not enabled.
                // auth/too-many-requests: Too many attempts to sign in have been made.
                console.error('Error updating password: ', error);
                throw new Error('Error updating password: ' + error.message);
              });
          })
          .catch((error) => {
            console.error('Error retrieving credential: ', error);
            throw new Error('Error retrieving credential: ' + error.message);
          });
      } catch (error) {
        console.error('Error: ', error);
        Sentry.captureException(error);
      }
    }
  }

  function handleCancel() {
    setErrors([]);
    setRequestingUpdate(false);
  }

  function onChangePasswordField(password: string) {
    setCurrentPassword(password);
  }
  function onChangeNewPassword1(password: string) {
    setNewPassword1(password);
  }
  function onChangeNewPassword2(password: string) {
    setNewPassword2(password);
  }

  return (
    <div className="mt-6">
      {requestingUpdate === false && (
        <Button
          id="update-password"
          type="button"
          textContent="Update Password"
          onClick={() => setRequestingUpdate(true)}
        />
      )}

      {requestingUpdate && (
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <div className="mt-1">
              <PasswordInput
                id="password"
                title="Current Password"
                password={currentPassword}
                onChange={(e) => onChangePasswordField(e.target.value)}
              />
              <PasswordInput
                id="newPassword1"
                title="New Password"
                password={newPassword1}
                onChange={(e) => onChangeNewPassword1(e.target.value)}
              />
              <PasswordInput
                id="newPassword2"
                title="Verify New Password"
                password={newPassword2}
                onChange={(e) => onChangeNewPassword2(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Button id="request-update" type="submit" textContent="Update Password" />
          </div>
          {errors ? <p className="text-red-500">{errors}</p> : null}
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Button id="cancel" type="button" textContent="Cancel" onClick={handleCancel} />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
